import React, { useMemo } from "react";
import { TIMELINE_START, DAY_MS, HOUR_MS } from "./timeline-time";
import "./TimelineHistogram.css";

// Shortest bar for a day that has any stories, as a fraction of full height,
// so a quiet day still reads as "has data" next to a gap.
const MIN_BAR = 0.12;

const dayKeyOf = (ms) => new Date(ms).toISOString().slice(0, 10);

// The UTC days whose hourly counts the Day zoom's window needs (one or two).
export const histogramDays = (timeline) =>
  timeline.scale === "day" ? [...new Set([dayKeyOf(timeline.start), dayKeyOf(timeline.start + DAY_MS - 1)])] : [];

// [from, to, count] spans in slider positions, one per day with stories in
// the All and Month zooms (day index k spans k ± ½), one per UTC hour in the
// Day zoom (positions are hours since the window start), clipped to the range.
function spans(timeline, dayCounts, hourCounts) {
  const out = [];
  if (timeline.scale !== "day") {
    if (!dayCounts) return out;
    for (let k = timeline.min; k <= timeline.max; k++) {
      out.push([k - 0.5, k + 0.5, dayCounts[dayKeyOf(TIMELINE_START + k * DAY_MS)] || 0]);
    }
    return out;
  }
  const firstHour = Math.floor(timeline.start / HOUR_MS) * HOUR_MS;
  for (let t = firstHour; t < timeline.start + DAY_MS; t += HOUR_MS) {
    const hours = hourCounts?.[dayKeyOf(t)];
    if (!hours) continue;
    const from = Math.max(timeline.min, (t - timeline.start) / HOUR_MS);
    const to = Math.min(timeline.max, (t + HOUR_MS - timeline.start) / HOUR_MS);
    if (to > from) out.push([from, to, hours[new Date(t).getUTCHours()] || 0]);
  }
  return out;
}

// [from, to, height 0–1] for each span with stories. Heights follow the
// square root of the count, relative to the busiest span in view, so busy
// days don't flatten everything else.
export function histogramBars(timeline, dayCounts, hourCounts) {
  const all = spans(timeline, dayCounts, hourCounts);
  const peak = Math.max(0, ...all.map((s) => s[2]));
  return all.filter((s) => s[2] > 0).map(([from, to, n]) => [from, to, MIN_BAR + (1 - MIN_BAR) * Math.sqrt(n / peak)]);
}

// Bars of stories per day (per hour in the Day zoom) drawn along the slider
// rail, like the Wayback Machine's capture histogram, so gaps in the archive
// show before you land in them.
export default function TimelineHistogram({ timeline, dayCounts, hourCounts }) {
  const bars = useMemo(() => histogramBars(timeline, dayCounts, hourCounts), [timeline, dayCounts, hourCounts]);
  if (!bars.length) return null;
  const span = Math.max(1, timeline.max - timeline.min);
  // One viewBox unit per day or hour, so bars sit on their slider positions;
  // a hairline gap between them while they are wide enough to show it.
  const inset = span <= 60 ? 0.08 : 0;
  const d = bars.map(([from, to, h]) =>
    `M${from - timeline.min + inset} 1V${1 - h}H${to - timeline.min - inset}V1z`).join("");
  return (
    <svg className="timeline-histogram" viewBox={`0 0 ${span} 1`} preserveAspectRatio="none" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}
